import { useCallback, useState } from 'react';
import filter from 'lodash/filter';
import size from 'lodash/size';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';

import { SubmitSourceFilesToTaskFormData } from '../../SubmitSourceFilesToTaskForm.types';
import {
  TaskFields,
  TaskNanoID,
  TaskProjectNanoID,
  TaskSourceFilesFields
} from '../../../../../tasksTypes';

import {
  SUBMIT_SOURCE_FILES_TO_TASK_QUERY,
  SubmitSourceFilesToTaskQueryResponse
} from '../../../../../queries/submitSourceFilesToTask.query';
import {
  SUBMIT_FILE_ATTACHMENTS_AS_SOURCE_FILES_TO_TASK_QUERY,
  SubmitFileAttachmentsAsSourceFilesToTaskResponse
} from '../../../../../queries/submitFileAttachmentsAsSourceFilesToTask.query';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';
import { useSubmitSourceFilesToTask } from '../../../../../hooks/useSubmitSourceFilesToTask';
import { useSubmitFileAttachmentsAsSourceFilesToTask } from '../../../../../hooks/useSubmitFileAttachmentsAsSourceFileToTask';

import {
  DropzoneHelperFileId,
  DropzoneHelperFiles
} from '../../../../../../../helpers/dropzone/DropzoneHelper';

import { TaskCache } from '../../../../../TaskCache';
import { ProjectCache } from '../../../../../../projects/ProjectCache';
import { formsErrors } from '../../../../../../../locales/keys';

const submitSourceFilesToTaskFormData: SubmitSourceFilesToTaskFormData = {
  sourceFileIds: [],
  autoSubmit: false
};

interface SubmitSourceFilesToTaskFormOptions {
  taskNanoId: TaskNanoID;
  projectNanoId?: TaskProjectNanoID;
  initialFileIds?: DropzoneHelperFileId[];
}

const sourceFileIdsRules = { required: formsErrors.required };

function useSubmitSourceFilesToTaskForm({
  taskNanoId,
  projectNanoId,
  initialFileIds
}: SubmitSourceFilesToTaskFormOptions) {
  const { handleSubmitReactHookForm, register, errors, control, resetForm } =
    useReactHookForm<SubmitSourceFilesToTaskFormData>({
      defaultValues: submitSourceFilesToTaskFormData,
      isModalForm: true
    });

  const {
    submitSourceFilesToTask,
    submitSourceFilesToTaskLoading,
    submitSourceFilesToTaskErrorMessage,
    submitSourceFilesToTaskReset
  } = useSubmitSourceFilesToTask<SubmitSourceFilesToTaskQueryResponse>({
    query: SUBMIT_SOURCE_FILES_TO_TASK_QUERY,
    cacheKeys: projectNanoId
      ? [
          ProjectCache.tasksCacheKey(projectNanoId),
          ProjectCache.tasksCompactCacheKey(projectNanoId),
          ProjectCache.tasksResultsCacheKey(projectNanoId)
        ]
      : [
          TaskCache.showCacheKey(),
          TaskCache.showResultsCacheKey(),
          TaskCache.indexCacheKey(),
          TaskCache.sourceFilesCacheKey(taskNanoId),
          TaskCache.resultsCacheKey(taskNanoId),
          TaskCache.itemsResultsCacheKey(),
          TaskCache.withoutSourceFiles(),
          TaskCache.dashboardWithoutSourceFiles(),
          TaskCache.userResultsCacheKey(),
          TaskCache.showSidebarCacheKey()
        ]
  });

  const {
    submitFileAttachmentsAsSourceFilesToTask,
    submitFileAttachmentsAsSourceFilesToTaskLoading,
    submitFileAttachmentsAsSourceFilesToTaskErrorMessage,
    submitFileAttachmentsAsSourceFilesToTaskReset
  } = useSubmitFileAttachmentsAsSourceFilesToTask<SubmitFileAttachmentsAsSourceFilesToTaskResponse>(
    {
      query: SUBMIT_FILE_ATTACHMENTS_AS_SOURCE_FILES_TO_TASK_QUERY,
      cacheKeys: [
        TaskCache.showCacheKey(),
        TaskCache.indexCacheKey(),
        TaskCache.sourceFilesCacheKey(taskNanoId),
        TaskCache.resultsCacheKey(taskNanoId),
        TaskCache.itemsResultsCacheKey(),
        TaskCache.withoutSourceFiles(),
        TaskCache.dashboardWithoutSourceFiles(),
        TaskCache.userResultsCacheKey()
      ]
    }
  );

  const [loadingFiles, setLoadingFiles] = useState<number>(0);
  const [allFiles, setAllFiles] = useState<number>(0);

  const handleChangeFiles = useCallback<(files: DropzoneHelperFiles) => void>(
    (files) => {
      const totalFiles = size(files);
      setLoadingFiles(
        totalFiles - size(filter(files, (file) => file.state === 'finished'))
      );
      setAllFiles(totalFiles);
    },
    []
  );

  return {
    control,
    submitSourceFilesToTask,
    submitSourceFilesToTaskLoading:
      submitSourceFilesToTaskLoading ||
      submitFileAttachmentsAsSourceFilesToTaskLoading,
    submitSourceFilesToTaskErrorMessage:
      submitSourceFilesToTaskErrorMessage ||
      submitFileAttachmentsAsSourceFilesToTaskErrorMessage,
    submitSourceFilesToTaskReset:
      submitSourceFilesToTaskReset ||
      submitFileAttachmentsAsSourceFilesToTaskReset,
    resetForm,
    handleSubmitSourceFilesToTask: handleSubmitReactHookForm({
      onSubmit: async (data: SubmitSourceFilesToTaskFormData) => {
        const sourceFileIds = filter(
          data.sourceFileIds,
          (sourceFileId) => !includes(initialFileIds, sourceFileId)
        );
        const fileAttachmentIds = filter(data.sourceFileIds, (sourceFileId) =>
          includes(initialFileIds, sourceFileId)
        );

        if (!isEmpty(fileAttachmentIds)) {
          await submitFileAttachmentsAsSourceFilesToTask({
            uuid: taskNanoId,
            fileAttachmentIds,
            moveToDeepArchive: false
          });
        }

        if (size(sourceFileIds) > 0) {
          return submitSourceFilesToTask({
            uuid: taskNanoId,
            sourceFileIds,
            moveToDeepArchive: false
          });
        }
      }
    }),
    registerSourceFileIds: register(
      TaskFields.SOURCE_FILE_IDS,
      sourceFileIdsRules
    ),
    registerAutoSubmit: register(TaskSourceFilesFields.AUTO_SUBMIT),
    validationErrors: {
      sourceFileIdsValidationError: errors?.sourceFileIds?.['message']
    },
    allFiles,
    loadingFiles,
    handleChangeFiles
  };
}

export default useSubmitSourceFilesToTaskForm;
